// Vue core imports
import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'

// Configuration and service imports
import { TENANCY_SETTINGS } from '@/config'
import API from './config'
import ApiService from './services/api.service'

// Third-party library imports
import axios from 'axios'
import VueAxios from 'vue-axios'
import Vue3Toastify, { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import '@/assets/file-input.css'
import CoreuiVue from '@coreui/vue'
import CIcon from '@coreui/icons-vue'
import { iconsSet as icons } from '@/assets/icons'

// Plugin imports
import i18n from '@/plugins/i18n'
import PointsImporter from '@/plugins/points-importer'
import CustomerNumbersAssistant from '@/plugins/customer-numbers-assistant'

// Filters
import filters from '@/helpers/filters'

// Component imports
import DocsCallout from '@/components/DocsCallout'
import DocsExample from '@/components/DocsExample'

const BUILD_VERSION_POLL_MS = 5 * 60 * 1000
const BUILD_VERSION_RELOAD_KEY = 'app_build_reloaded_for'

const getCurrentBuildVersion = () => {
  const meta = document.querySelector('meta[name="app-version"]')
  if (meta && meta.content) {
    return meta.content
  }
  return process.env.VUE_APP_BUILD_VERSION || null
}

const fetchLatestBuildVersion = async () => {
  const baseUrl = process.env.BASE_URL || '/'
  const response = await fetch(`${baseUrl}version.json?cache-bust=${Date.now()}`, {
    cache: 'no-store',
  })
  const data = await response.json()
  return data && data.version ? String(data.version) : null
}

// Compare version.json with current build and reload stale bundles automatically
const ensureLatestBuild = async () => {
  if (process.env.NODE_ENV !== 'production') {
    return true
  }

  const currentBuildVersion = getCurrentBuildVersion()
  if (!currentBuildVersion) {
    return true
  }

  try {
    const latestVersion = await fetchLatestBuildVersion()
    if (latestVersion && latestVersion !== currentBuildVersion) {
      const reloadedFor = sessionStorage.getItem(BUILD_VERSION_RELOAD_KEY)
      if (reloadedFor !== latestVersion) {
        sessionStorage.setItem(BUILD_VERSION_RELOAD_KEY, latestVersion)
        window.location.reload()
        return false
      }
    }
  } catch (error) {
    console.warn('Version check failed, continuing with current bundle', error)
  }

  return true
}

const scheduleBuildVersionChecks = () => {
  setInterval(() => {
    ensureLatestBuild()
  }, BUILD_VERSION_POLL_MS)

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      ensureLatestBuild()
    }
  })
}

// Utility function to extract country code from URL
const extractCountryCode = (url) => {
  const countryMatch = url.match(/https:\/\/(?:dev)?admin-plus-([a-z]{2})\.bsplus\.bridgestone\.eu/i)
  return countryMatch && countryMatch[1] ? countryMatch[1].toUpperCase() : null
}

const loadTenantConfig = async (countryCode) => {
  try {
    if (countryCode) {
      store.commit('SET_TENANT_ID', countryCode)
      store.commit('SET_TENANCY_ENABLED', true)
    }

    ApiService.init(API.URL)
  } catch (error) {
    console.error('Error loading tenant configuration from API:', error)
  }
}

const detectCountryAndLoadConfig = async () => {
  let url = window.location.href
  let countryCode = null
  if (TENANCY_SETTINGS.TENANCY) {
    if (TENANCY_SETTINGS.TENANCY_LOCAL) {
      countryCode = TENANCY_SETTINGS.TENANCY_COUNTRY
    } else {
      countryCode = extractCountryCode(url)
      if (countryCode) {
        console.log('Detected Country Code:', countryCode) // Log country code to validate extraction
      }
    }
    await loadTenantConfig(countryCode)
  }
}

// Detect country and load tenant configuration
const bootstrap = async () => {
  const isLatest = await ensureLatestBuild()
  if (!isLatest) {
    return
  }

  await detectCountryAndLoadConfig()

  // Initialize API Service (if not already initialized in loadTenantConfig)
  if (!store.getters.tenantId) {
    ApiService.init(API.URL)
  }

  // Create Vue app and use necessary plugins
  const app = createApp(App)

  // Register store, router, and i18n
  app.use(store)
  app.use(router)
  app.use(i18n)

  // Register UI libraries and components
  app.use(CoreuiVue)
  app.use(VueAxios, axios)
  app.component('CIcon', CIcon)
  app.component('DocsCallout', DocsCallout)
  app.component('DocsExample', DocsExample)

  // Register additional plugins
  app.use(Vue3Toastify, {
    autoClose: 3000,
    position: toast.POSITION.TOP_RIGHT,
    transition: toast.TRANSITIONS.SLIDE,
  })
  app.use(PointsImporter, {store, i18n})
  app.use(CustomerNumbersAssistant, {store, i18n})

  // Provide icons globally
  app.provide('icons', icons)

  app.config.globalProperties.$filters = filters
  // Mount the app
  app.mount('#app')

  const loader = document.getElementById('app-loader')
  if (loader && loader.parentNode) {
    loader.parentNode.removeChild(loader)
  }

  scheduleBuildVersionChecks()
}

bootstrap().catch((error) => {
  console.error('Error during initialization:', error)
})
